import type {
  CreateVoucherPayload,
  IVoucher,
  UpdateVoucherPayload,
  VoucherListParams,
} from '@/shared/api/types/voucher-types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createVoucher,
  deleteVoucher,
  getVoucherById,
  getVouchers,
  updateVoucher,
} from '@/shared/api/services/voucher-service'

export const VOUCHER_QUERY_KEYS = {
  all: ['vouchers'] as const,
  listPrefix: () => [...VOUCHER_QUERY_KEYS.all, 'list'] as const,
  list: (tenantId: string, params: VoucherListParams) =>
    [...VOUCHER_QUERY_KEYS.all, 'list', tenantId, params] as const,
  detailPrefix: (id: string) => [...VOUCHER_QUERY_KEYS.all, 'detail', id] as const,
  detail: (id: string) => [...VOUCHER_QUERY_KEYS.all, 'detail', id] as const,
}

export function useVouchersQuery(
  tenantId: string,
  params: VoucherListParams,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: VOUCHER_QUERY_KEYS.list(tenantId, params),
    queryFn: async () => getVouchers(tenantId, params),
    enabled: options?.enabled ?? true,
    staleTime: 0,
  })
}

export function useVoucherByIdQuery(id: string) {
  return useQuery({
    queryKey: VOUCHER_QUERY_KEYS.detail(id),
    queryFn: async () => getVoucherById(id),
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function useCreateVoucherMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CreateVoucherPayload) => createVoucher(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: VOUCHER_QUERY_KEYS.all })
    },
  })
}

export function useUpdateVoucherMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateVoucherPayload }) =>
      updateVoucher(id, payload),
    onSuccess: async (_data, { id }) => {
      await queryClient.invalidateQueries({ queryKey: VOUCHER_QUERY_KEYS.listPrefix() })
      await queryClient.invalidateQueries({ queryKey: VOUCHER_QUERY_KEYS.detailPrefix(id) })
    },
  })
}

export function useDeleteVoucherMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => deleteVoucher(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: VOUCHER_QUERY_KEYS.all })
    },
  })
}

export type { IVoucher }
